/**
 * Demo 模式数据服务 — 只读 + 内存修改版（替代原先 1282 行的 local-price-intel-service.ts）。
 *
 * 设计原则：
 * 1. 所有初始数据由 `fixtures/demo.json` 提供，演示汇报/审核台使用。
 * 2. 任何"上传/审核/发布"在 demo 模式下只改动内存副本，不会落库、不会调外部 API。
 * 3. 组件层通过 `price-intel-service.ts` 统一入口调用，无需区分 demo/real 细节。
 */

import demoSeed from '../fixtures/demo';
import {
  buildPdfStructure,
  extractSpecDraftByRules,
  type PdfTextToken,
} from './spec-extraction-engine';
import {
  mapSpecsToProductSpecs,
  inferCompetitorFromText,
} from './spec-to-candidate-mapper';
import type {
  PriceIntelBriefing,
  PriceIntelCandidateFact,
  PriceIntelCandidateUpdateInput,
  PriceIntelCompetitorOverview,
  PriceIntelIngestionJob,
  PriceIntelManualCandidateInput,
  PriceIntelPitchFinderRow,
  PriceIntelPriceFact,
  PriceIntelProductOverview,
  PriceIntelSourceCreateResponse,
  PriceIntelSourceRecord,
  PriceIntelTrendResponse,
  PriceIntelPreviewResult,
} from '../types';

interface DemoState {
  jobs: PriceIntelIngestionJob[];
  candidates: PriceIntelCandidateFact[];
  price_facts: PriceIntelPriceFact[];
  briefings: PriceIntelBriefing[];
  trends: PriceIntelTrendResponse;
  competitor_overviews: PriceIntelCompetitorOverview[];
  product_overviews: PriceIntelProductOverview[];
  pitch_finder: PriceIntelPitchFinderRow[];
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const initialState = (): DemoState => clone(demoSeed as unknown as DemoState);

let state: DemoState = initialState();

const generateId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

const now = () => new Date().toISOString();

export async function fetchLocalJobs(status?: string): Promise<PriceIntelIngestionJob[]> {
  const jobs = clone(state.jobs);
  return status ? jobs.filter((job) => job.status === status) : jobs;
}

export async function fetchLocalCandidates(
  reviewStatus = 'pending_review',
): Promise<PriceIntelCandidateFact[]> {
  return clone(state.candidates.filter((c) => c.review_status === reviewStatus));
}

export async function updateLocalCandidate(
  id: string,
  payload: PriceIntelCandidateUpdateInput,
): Promise<PriceIntelCandidateFact> {
  const target = state.candidates.find((c) => c.id === id);
  if (!target) throw new Error('候选事实不存在');
  Object.assign(target, payload, { updated_at: now() });
  return clone(target);
}

export async function approveLocalCandidate(id: string): Promise<{ price_fact_id: string }> {
  const candidate = state.candidates.find((c) => c.id === id);
  if (!candidate) throw new Error('候选事实不存在');
  candidate.review_status = 'approved';
  candidate.reviewed_at = now();

  const factId = generateId('demo-fact');
  state.price_facts.unshift({
    id: factId,
    source_record_id: candidate.source_record_id,
    candidate_fact_id: candidate.id,
    competitor: candidate.competitor || '',
    series: candidate.series ?? null,
    model: candidate.model ?? null,
    pitch_mm: candidate.pitch_mm ?? null,
    brightness: candidate.brightness ?? null,
    application: candidate.application ?? null,
    indoor_outdoor: candidate.indoor_outdoor ?? null,
    region: candidate.region ?? null,
    country: candidate.country ?? null,
    price_per_piece: candidate.price_per_piece ?? null,
    price_per_sqm: candidate.price_per_sqm ?? null,
    currency: candidate.currency ?? null,
    year_quarter: candidate.year_quarter ?? null,
    notes: candidate.notes ?? null,
    confidence: candidate.confidence ?? null,
    source_excerpt: candidate.source_excerpt ?? null,
    source_page: candidate.source_page ?? null,
    created_at: now(),
    updated_at: now(),
    published_at: now(),
  });
  candidate.approved_price_fact_id = factId;
  return { price_fact_id: factId };
}

export async function rejectLocalCandidate(
  id: string,
  review_comment?: string,
): Promise<PriceIntelCandidateFact> {
  const candidate = state.candidates.find((c) => c.id === id);
  if (!candidate) throw new Error('候选事实不存在');
  candidate.review_status = 'rejected';
  candidate.review_comment = review_comment || null;
  candidate.reviewed_at = now();
  return clone(candidate);
}

export async function fetchLocalPriceFacts(filters?: {
  competitor?: string;
  region?: string;
  country?: string;
  year_quarter?: string;
}): Promise<PriceIntelPriceFact[]> {
  return clone(state.price_facts).filter((fact) => {
    if (filters?.competitor && fact.competitor !== filters.competitor) return false;
    if (filters?.region && fact.region !== filters.region) return false;
    if (filters?.country && fact.country !== filters.country) return false;
    if (filters?.year_quarter && fact.year_quarter !== filters.year_quarter) return false;
    return true;
  });
}

export async function fetchLocalTrends(filters?: {
  competitor?: string;
  region?: string;
  country?: string;
}): Promise<PriceIntelTrendResponse> {
  const all = clone(state.trends);
  if (!filters?.competitor) return all;
  return {
    filters: filters || {},
    series: all.series.filter((s) => s.competitor === filters.competitor),
  };
}

export async function fetchLocalBriefings(): Promise<PriceIntelBriefing[]> {
  return clone(state.briefings);
}

export async function createLocalBriefing(payload: {
  title: string;
  reporting_period?: string;
  competitor?: string;
  region?: string;
  country?: string;
}): Promise<PriceIntelBriefing> {
  const briefing: PriceIntelBriefing = {
    id: generateId('demo-briefing'),
    title: payload.title,
    reporting_period: payload.reporting_period || null,
    filters: {
      competitor: payload.competitor,
      region: payload.region,
      country: payload.country,
    },
    summary_markdown: `## Demo 快照\n\n- 生成时间：${now()}\n- 仅演示用，真实汇报请切换到在线模式。\n`,
    chart_payload: {},
    created_at: now(),
    updated_at: now(),
  };
  state.briefings.unshift(briefing);
  return clone(briefing);
}

export async function seedLocalPriceIntelData(): Promise<void> {
  state = initialState();
}

export async function clearLocalPriceIntelData(): Promise<void> {
  state = {
    jobs: [],
    candidates: [],
    price_facts: [],
    briefings: [],
    trends: { series: [], filters: {} },
    competitor_overviews: [],
    product_overviews: [],
    pitch_finder: [],
  };
}

export async function fetchLocalCompetitorOverviews(): Promise<PriceIntelCompetitorOverview[]> {
  return clone(state.competitor_overviews).sort((a, b) =>
    a.competitor_name.localeCompare(b.competitor_name),
  );
}

export async function fetchLocalProductOverviews(
  competitorId?: string,
): Promise<PriceIntelProductOverview[]> {
  const list = clone(state.product_overviews);
  return list
    .filter((p) => (competitorId ? p.competitor_id === competitorId : true))
    .sort((a, b) => {
      const byName = a.competitor_name.localeCompare(b.competitor_name);
      if (byName !== 0) return byName;
      return (a.pitch_mm ?? 0) - (b.pitch_mm ?? 0);
    });
}

export async function fetchLocalPitchFinder(options: {
  pitchMin?: number;
  pitchMax?: number;
  indoorOutdoor?: string;
  application?: string;
}): Promise<PriceIntelPitchFinderRow[]> {
  return clone(state.pitch_finder)
    .filter((row) => {
      if (options.pitchMin != null && row.pitch_mm < options.pitchMin) return false;
      if (options.pitchMax != null && row.pitch_mm > options.pitchMax) return false;
      if (options.indoorOutdoor && row.indoor_outdoor !== options.indoorOutdoor) return false;
      if (options.application && row.application !== options.application) return false;
      return true;
    })
    .sort((a, b) => a.pitch_mm - b.pitch_mm);
}

function buildSourceRecord(
  sourceKind: 'file' | 'text',
  sourceName: string,
  input_text?: string,
): PriceIntelSourceRecord {
  return {
    id: generateId('demo-source'),
    source_kind: sourceKind,
    source_name: sourceName,
    input_text: input_text ?? null,
    metadata: {},
    created_at: now(),
    updated_at: now(),
  };
}

function buildQueuedJob(sourceRecord: PriceIntelSourceRecord): PriceIntelIngestionJob {
  return {
    id: generateId('demo-job'),
    source_record_id: sourceRecord.id,
    status: 'queued',
    job_type: 'demo_only',
    retry_count: 0,
    parser_pipeline: ['demo'],
    result_summary: { source_name: sourceRecord.source_name },
    created_at: now(),
    updated_at: now(),
  };
}

export async function createLocalSourceFromText(payload: {
  title?: string;
  input_text: string;
  metadata?: Record<string, unknown>;
}): Promise<PriceIntelSourceCreateResponse> {
  const record = buildSourceRecord('text', payload.title || 'Demo Text Source', payload.input_text);
  const job = buildQueuedJob(record);
  state.jobs.unshift(job);
  return { source_record: record, ingestion_job: job };
}

export async function createLocalManualCandidate(
  payload: PriceIntelManualCandidateInput,
): Promise<PriceIntelCandidateFact> {
  const record = buildSourceRecord('text', payload.source_name, payload.source_excerpt ?? undefined);
  record.metadata = { ...(record.metadata || {}), intake: 'manual_entry' };

  const job: PriceIntelIngestionJob = {
    id: generateId('demo-job-manual'),
    source_record_id: record.id,
    status: 'succeeded',
    job_type: 'manual_entry',
    retry_count: 0,
    parser_pipeline: ['manual_entry'],
    result_summary: { source_name: payload.source_name, origin: 'manual', candidates: 1 },
    claimed_by: 'manual',
    claimed_at: now(),
    started_at: now(),
    finished_at: now(),
    created_at: now(),
    updated_at: now(),
  };
  state.jobs.unshift(job);

  const candidate: PriceIntelCandidateFact = {
    id: generateId('demo-cand-manual'),
    source_record_id: record.id,
    ingestion_job_id: job.id,
    competitor: payload.competitor,
    series: payload.series ?? null,
    model: payload.model ?? null,
    pitch_mm: payload.pitch_mm ?? null,
    brightness: payload.brightness ?? null,
    application: payload.application ?? null,
    indoor_outdoor: payload.indoor_outdoor ?? null,
    region: payload.region ?? null,
    country: payload.country ?? null,
    price_per_piece: payload.price_per_piece ?? null,
    price_per_sqm: payload.price_per_sqm ?? null,
    currency: payload.currency ?? null,
    year_quarter: payload.year_quarter ?? null,
    notes: payload.notes ?? null,
    source_excerpt: payload.source_excerpt ?? null,
    competitor_id: payload.competitor_id ?? null,
    product_model_id: payload.product_model_id ?? null,
    confidence: 1,
    normalized_payload: { origin: 'manual_entry' },
    review_status: 'pending_review',
    created_at: now(),
    updated_at: now(),
    source_record: {
      id: record.id,
      source_name: record.source_name,
      source_kind: 'text',
    },
  };
  state.candidates.unshift(candidate);

  return clone(candidate);
}

export async function createLocalSourceFromBrowserFile(input: {
  file: File;
  fileId?: string;
  sourceName?: string;
  storageKey?: string;
  metadata?: Record<string, unknown>;
}): Promise<PriceIntelSourceCreateResponse> {
  const record = buildSourceRecord('file', input.sourceName || input.file.name);
  record.mime_type = input.file.type || 'application/octet-stream';
  record.file_storage_key = input.storageKey ?? null;
  if (input.fileId) record.file_id = input.fileId;
  const job = buildQueuedJob(record);
  state.jobs.unshift(job);
  return { source_record: record, ingestion_job: job };
}

// =============================================================================
// 就地解析（Local Inline Parse）
// -----------------------------------------------------------------------------
// 旧实现的痛点：上传文件 → 直接丢一条 queued job 到列表 → 本地模式没 worker →
// 永远停在"排队中"，对用户不友好。
//
// 新实现：parseLocalSourcePreview 只"模拟"一次解析结果并返回给前端，不写入
// state。前端展示候选预览，用户确认后再调 commitLocalParsedPreview 把 source +
// succeeded job + pending candidates 一次性落库，随后审核台就能看到。
// =============================================================================

// PriceIntelPreviewResult 现在从 ../types 导入
interface ParseLocalInput {
  kind: 'file' | 'text';
  file?: File;
  sourceName?: string;
  inputText?: string;
  metadata?: Record<string, unknown>;
}

const COMPETITOR_HINTS: Array<{
  match: RegExp;
  competitor: string;
  series?: string;
  model?: string;
  pitch_mm?: number;
  application?: string;
  indoor_outdoor?: 'Indoor' | 'Outdoor';
}> = [
  { match: /absen|艾比森/i, competitor: 'Absen', series: 'A25', model: 'A25 Outdoor', pitch_mm: 2.5, application: 'Rental', indoor_outdoor: 'Outdoor' },
  { match: /leyard|洲磊|利亚/i, competitor: 'Leyard', series: 'CarbonLight', model: 'CLI 2.6', pitch_mm: 2.6, application: 'Rental', indoor_outdoor: 'Indoor' },
  { match: /unilumin|洲明|umini|upad/i, competitor: 'Unilumin', series: 'Umini', model: 'Umini III 0.9', pitch_mm: 0.9375, application: 'Fine Pitch', indoor_outdoor: 'Indoor' },
  { match: /roe\s*visual|roe|verity|ruby/i, competitor: 'ROE Visual', series: 'Verity', model: 'V2.6', pitch_mm: 2.6, application: 'Fixed Install', indoor_outdoor: 'Indoor' },
  { match: /samsung|三星/i, competitor: 'Samsung', series: 'IAB', model: 'IAB 1.2', pitch_mm: 1.26, application: 'Control Room', indoor_outdoor: 'Indoor' },
  { match: /lg/i, competitor: 'LG', series: 'LSAB', model: 'LSAB 1.5', pitch_mm: 1.5, application: 'Fine Pitch', indoor_outdoor: 'Indoor' },
  { match: /fabulux|法布/i, competitor: 'Fabulux', series: 'FX', model: 'FX 3.9', pitch_mm: 3.9, application: 'Rental', indoor_outdoor: 'Outdoor' },
];

const PITCH_REGEX = /(\d+(?:[\.,]\d+)?)\s*mm/i;
const PRICE_SQM_REGEX = /(usd|rmb|cny|eur|\$)\s*([\d,]+(?:\.\d+)?)\s*(?:\/|per)\s*(?:sqm|m2|㎡|平方|square meter)/i;
const PRICE_PIECE_REGEX = /(usd|rmb|cny|eur|\$)\s*([\d,]+(?:\.\d+)?)\s*(?:\/|per)\s*(?:piece|pcs|片|模组|cabinet|箱体)/i;
const QUARTER_REGEX = /(20\d{2})\s*[\-\s]?\s*Q([1-4])/i;

function detectFromText(text: string): Array<{
  competitor: string;
  series?: string;
  model?: string;
  pitch_mm?: number;
  application?: string;
  indoor_outdoor?: 'Indoor' | 'Outdoor';
}> {
  const hits = COMPETITOR_HINTS.filter((h) => h.match.test(text));
  if (hits.length > 0) return hits.map(({ match: _m, ...rest }) => rest).slice(0, 3);
  return [
    {
      competitor: 'Unknown Brand',
      model: undefined,
    },
  ];
}

function parseNumber(raw: string): number | null {
  const cleaned = raw.replace(/,/g, '').trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function normalizeCurrency(raw?: string): string | null {
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (upper === '$') return 'USD';
  if (upper === 'RMB' || upper === 'CNY') return 'CNY';
  return upper;
}

async function buildPreviewCandidates(
  source: PriceIntelSourceRecord,
  jobId: string,
  rawText: string,
): Promise<PriceIntelCandidateFact[]> {
  const sampleText = rawText.slice(0, 4000);
  const quarterMatch = QUARTER_REGEX.exec(sampleText);
  const yearQuarter = quarterMatch ? `${quarterMatch[1]}Q${quarterMatch[2]}` : null;

  const pitchMatch = PITCH_REGEX.exec(sampleText);
  const detectedPitch = pitchMatch ? parseNumber(pitchMatch[1].replace(',', '.')) : null;

  const priceSqmMatch = PRICE_SQM_REGEX.exec(sampleText);
  const priceSqm = priceSqmMatch ? parseNumber(priceSqmMatch[2]) : null;
  const currencyFromSqm = priceSqmMatch ? normalizeCurrency(priceSqmMatch[1]) : null;

  const pricePieceMatch = PRICE_PIECE_REGEX.exec(sampleText);
  const pricePiece = pricePieceMatch ? parseNumber(pricePieceMatch[2]) : null;
  const currencyFromPiece = pricePieceMatch ? normalizeCurrency(pricePieceMatch[1]) : null;

  const currency = currencyFromSqm || currencyFromPiece || (rawText ? 'USD' : null);

  const hits = detectFromText(sampleText + ' ' + source.source_name);
  const nowIso = now();

  return hits.map((hit, index) => {
    const excerpt = sampleText
      ? sampleText.split(/[\n。\.]/)[0]?.trim().slice(0, 180) || sampleText.slice(0, 180)
      : `从文件 ${source.source_name} 中抽取的候选（未匹配到正文，使用文件名启发）。`;

    return {
      id: `demo-cand-preview-${Math.random().toString(36).slice(2, 9)}-${index}`,
      source_record_id: source.id,
      ingestion_job_id: jobId,
      competitor: hit.competitor,
      series: hit.series ?? null,
      model: hit.model ?? null,
      pitch_mm: hit.pitch_mm ?? detectedPitch,
      brightness: null,
      application: hit.application ?? null,
      indoor_outdoor: hit.indoor_outdoor ?? null,
      region: null,
      country: null,
      price_per_piece: pricePiece,
      price_per_sqm: priceSqm,
      currency,
      year_quarter: yearQuarter,
      notes: `由本地演示解析器基于 ${source.source_kind === 'file' ? '文件名 + 抽样文本' : '粘贴文本'} 生成，置信度较低，请审核台核对。`,
      confidence: 0.6 - index * 0.1,
      source_excerpt: excerpt,
      source_page: source.source_kind === 'file' ? index + 1 : null,
      competitor_id: null,
      product_model_id: null,
      normalized_payload: { extractor: 'local_demo_preview' },
      review_status: 'pending_review' as const,
      created_at: nowIso,
      updated_at: nowIso,
      source_record: { id: source.id, source_name: source.source_name, source_kind: source.source_kind },
    };
  });
}

async function readFileAsText(file: File): Promise<string> {
  const textTypes = /(text|csv|plain|markdown|xml|json)/i;
  if (file.size > 512 * 1024) return '';
  if (!textTypes.test(file.type) && !/\.(txt|csv|md|markdown|json)$/i.test(file.name)) {
    return '';
  }
  try {
    return await file.text();
  } catch {
    return '';
  }
}

function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

// =============================================================================
// 采集意图层（demo 模式）：复刻后端规则版意图识别，不调后端。
// 仅在 isLocalDemoMode 且后端 intent 端点不可达时作为降级使用。
// =============================================================================

export type LocalDocIntent = 'price_quote' | 'spec_sheet' | 'tender_checklist' | 'research_note' | 'general';

export interface LocalIntentVerdict {
  intent: LocalDocIntent;
  confidence: number;
  intent_source: 'rule' | 'llm' | 'user_override';
  reason: string;
  suggested_fields: string[];
  alternatives: string[];
  needs_user_confirm: boolean;
}

const INTENT_FIELDS: Record<LocalDocIntent, string[]> = {
  price_quote: ['competitor', 'series', 'model', 'pitch_mm', 'price_per_piece', 'price_per_sqm', 'currency', 'region', 'country', 'year_quarter'],
  spec_sheet: ['competitor', 'series', 'model', 'pitch_mm', 'brightness', 'application', 'indoor_outdoor'],
  tender_checklist: ['competitor', 'region', 'country', 'notes'],
  research_note: ['competitor', 'region', 'country', 'notes'],
  general: ['competitor', 'series', 'model', 'pitch_mm', 'price_per_sqm', 'currency'],
};

const ALL_INTENTS: LocalDocIntent[] = ['price_quote', 'spec_sheet', 'tender_checklist', 'research_note', 'general'];

function localVerdict(
  intent: LocalDocIntent,
  confidence: number,
  reason: string,
  needsUserConfirm: boolean,
): LocalIntentVerdict {
  return {
    intent,
    confidence,
    intent_source: 'rule',
    reason,
    suggested_fields: INTENT_FIELDS[intent],
    alternatives: ALL_INTENTS.filter((i) => i !== intent).slice(0, 4),
    needs_user_confirm: needsUserConfirm,
  };
}

/** 轻量规则意图识别（与后端 classify_intent 规则层同逻辑）。 */
export function classifyLocalIntent(text: string): LocalIntentVerdict {
  const sample = (text || '').slice(0, 20000);
  const priceSignals = (sample.match(/(usd|eur|cny|rmb|\$|¥|€)\s*[\d,]+/gi) || []).length;
  const specSignals = (sample.match(/specification|configuration|cabinet|pixel\s*pitch|brightness|module\s*size|refresh\s*rate|规格|参数|配置|点间距|亮度|刷新率/gi) || []).length;
  const checklistSignals = (sample.match(/checklist|requirement|shall|must\b|compliance|deviation|应答|偏离表|招标|投标|条款/gi) || []).length;
  const hasTable = /\|[^\n]+\|\s*\n\s*\|[\s:|-]+\|/.test(sample);
  const hasPriceTable = hasTable && priceSignals >= 1;
  const hasCompetitor = COMPETITOR_HINTS.some((h) => h.match.test(sample));
  const tableBlocks = (sample.match(/\|[^\n]+\|\s*\n\s*\|[\s:|-]+\|/g) || []).length;

  if (priceSignals >= 2 && hasPriceTable) {
    return localVerdict('price_quote', 0.85, `检测到 ${priceSignals} 处价格信号且存在含价格的表格。`, false);
  }
  if (specSignals >= 3 && priceSignals === 0) {
    return localVerdict('spec_sheet', 0.82, `检测到 ${specSignals} 处规格参数信号、无价格信号（不猜价格）。`, false);
  }
  if (checklistSignals >= 2 && priceSignals < 2) {
    return localVerdict('tender_checklist', 0.7, `检测到 ${checklistSignals} 处条款/应答关键词。`, false);
  }
  if (tableBlocks === 0 && hasCompetitor && priceSignals < 2 && specSignals < 3) {
    return localVerdict('research_note', 0.62, '段落为主、有竞品提及但价格/规格信号零散。', true);
  }
  return localVerdict('general', 0.4, '未检测到明确信号，按通用文档处理。', true);
}

/** demo 模式下读取文件/文本的可分析文本（复用现有 readFileAsText 逻辑）。 */
export async function readLocalInputText(input: ParseLocalInput): Promise<string> {
  if (input.kind === 'text') return input.inputText || '';
  if (input.file) return readFileAsText(input.file);
  return '';
}

/**
 * 使用 pdfjs-dist 从 PDF 文件提取 tokens，然后通过规则引擎提取规格。
 * 这复用了管理面板的 Cabinet Specifications 提取方案，完整流程：
 *   pdf.js tokens → buildPdfStructure → extractSpecDraftByRules → mapSpecsToCandidates
 */
async function extractSpecsFromPdf(
  file: File,
  sourceRecord: PriceIntelSourceRecord,
): Promise<{ product_specs: import('../types').PriceIntelProductSpec[]; pipeline: string[] } | null> {
  try {
    const pdfjsLib = await import('pdfjs-dist');
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.mjs',
        import.meta.url,
      ).href;
    }

    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const tokens: PdfTextToken[] = [];

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      for (const item of content.items) {
        if (!('str' in item) || !item.str.trim()) continue;
        const tx = item.transform;
        tokens.push({
          page: pageNum,
          str: item.str,
          x: tx[4],
          y: tx[5],
          width: item.width ?? 0,
          height: tx[0] || 12,
        });
      }
    }

    if (!tokens.length) return null;

    const structure = buildPdfStructure(tokens, pdf.numPages);
    const competitor = inferCompetitorFromText(file.name) || '';
    const specs = extractSpecDraftByRules(structure, file.name, competitor);

    if (!specs.length) return null;

    // 映射为产品档案格式（不再走价格候选线）
    const product_specs = mapSpecsToProductSpecs(specs, {
      competitor,
      sourceName: file.name,
    });

    const pipeline = ['pdfjs_parse', 'rule_spec_extract', 'spec_to_product'];
    return { product_specs, pipeline };
  } catch (err) {
    console.warn('[price_intel] PDF spec extraction failed, falling back to regex:', err);
    return null;
  }
}

export async function parseLocalSourcePreview(
  input: ParseLocalInput,
): Promise<PriceIntelPreviewResult> {
  const record =
    input.kind === 'file' && input.file
      ? (() => {
          const r = buildSourceRecord('file', input.sourceName || input.file!.name);
          r.mime_type = input.file!.type || 'application/octet-stream';
          return r;
        })()
      : buildSourceRecord('text', input.sourceName || 'Pasted text', input.inputText);

  record.metadata = { ...(record.metadata || {}), ...(input.metadata || {}), intake: 'inline_preview' };

  const jobId = generateId('demo-job-preview');

  // PDF 文件：使用规则引擎提取产品规格
  if (input.kind === 'file' && input.file && isPdfFile(input.file)) {
    const specResult = await extractSpecsFromPdf(input.file, record);
    if (specResult && specResult.product_specs.length > 0) {
      return {
        source_record: record,
        product_specs: specResult.product_specs,
        price_candidates: [], // 规格书通常没有价格
        pipeline: specResult.pipeline,
      };
    }
    // 规则提取失败，fall through 到正则逻辑
  }

  // 非 PDF 或规则提取失败：走原有正则逻辑（生成价格候选）
  const pipeline =
    input.kind === 'file' ? ['download', 'decode_text', 'local_regex_extract'] : ['normalize_text', 'local_regex_extract'];

  const rawText =
    input.kind === 'text'
      ? input.inputText || ''
      : input.file
      ? await readFileAsText(input.file)
      : '';

  const candidates = await buildPreviewCandidates(record, jobId, rawText);
  return {
    source_record: record,
    product_specs: [],
    price_candidates: candidates,
    pipeline,
  };
}

export async function commitLocalParsedPreview(input: {
  preview: PriceIntelPreviewResult;
  candidateOverrides?: PriceIntelCandidateFact[];
}): Promise<{ source_record: PriceIntelSourceRecord; ingestion_job: PriceIntelIngestionJob; candidates: PriceIntelCandidateFact[] }> {
  const { preview, candidateOverrides } = input;
  const sourceRecord: PriceIntelSourceRecord = {
    ...preview.source_record,
    created_at: now(),
    updated_at: now(),
  };

  const finalCandidates = (candidateOverrides || preview.price_candidates).map((cand) => ({
    ...cand,
    source_record_id: sourceRecord.id,
  }));

  const job: PriceIntelIngestionJob = {
    id: generateId('demo-job'),
    source_record_id: sourceRecord.id,
    status: 'succeeded',
    job_type: preview.source_record.source_kind === 'file' ? 'parse_extract' : 'parse_text',
    retry_count: 0,
    parser_pipeline: preview.pipeline,
    result_summary: {
      source_name: sourceRecord.source_name,
      origin: 'inline_preview',
      candidates: finalCandidates.length,
    },
    claimed_by: 'inline-demo',
    claimed_at: now(),
    started_at: now(),
    finished_at: now(),
    created_at: now(),
    updated_at: now(),
  };

  const rebuiltCandidates = finalCandidates.map((cand) => ({
    ...cand,
    ingestion_job_id: job.id,
    source_record: {
      id: sourceRecord.id,
      source_name: sourceRecord.source_name,
      source_kind: sourceRecord.source_kind,
    },
  }));

  state.jobs.unshift(job);
  rebuiltCandidates.forEach((cand) => state.candidates.unshift(cand));

  return { source_record: sourceRecord, ingestion_job: job, candidates: clone(rebuiltCandidates) };
}
